// A simple hash function for the product ID to get a consistent seed value
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}

// Seeded random number generator function
function seededRandom(seed: number): () => number {
  return function (): number {
    const x = Math.sin(seed++) * 10000
    return x - Math.floor(x)
  }
}

function _complexLogic(base: number, factor: number, randomFunc: () => number): number {
  return Math.round(base * randomFunc() * factor)
}

function getBaseCountFromHash(hash: number, scale = 10, minThreshold = 1): number {
  return Math.abs(hash % scale) + minThreshold
}

interface ProductMetrics {
  soldCount: number
  repeatPurchaseCount: number
  currentlyViewingCount: number
}

export const useProductMetricsStore = defineStore('productMetrics', () => {
  const metricsCache = useState<Record<string, ProductMetrics>>(() => ({}))

  // Fixed timestamp values for consistent results
  const timestamp = Date.now()
  const hourOfDay = new Date(timestamp).getHours()
  const daySeed = Math.floor(timestamp / (1000 * 60 * 60 * 24))
  const minuteSeed = Math.floor(timestamp / (1000 * 60 * 5))

  // Get metrics for a product
  function getMetricsForProduct(productId: string): ProductMetrics {
    if (metricsCache.value[productId]) {
      return metricsCache.value[productId]
    }

    const productHash = hashString(productId) + daySeed
    const randomFunc = seededRandom(productHash)

    // Calculate baseCount
    const baseCountForSold = getBaseCountFromHash(productHash, 500, 250)
    const baseCountForRepeat = getBaseCountFromHash(productHash, 20, 10)
    const baseCountForViewing = getBaseCountFromHash(productHash, 50, 4)

    // Factor calculation
    const factorForSold = hourOfDay < 12 ? 0.8 : 1.5
    const factorForRepeat = (hourOfDay >= 12 && hourOfDay < 18) ? 1.2 : 0.9

    // Viewing count should be higher during peak hours (9AM-12PM and 6PM-9PM)
    const isPeakHour = (hourOfDay >= 9 && hourOfDay < 12) || (hourOfDay >= 18 && hourOfDay < 21)
    const factorForViewing = isPeakHour ? 1.7 : 0.7

    // Add small variation for viewing count that changes every few minutes
    const viewingVariation = seededRandom(productHash + minuteSeed)() * 0.3 + 0.85 // 0.85 to 1.15 range

    const generateCount = (baseCount: number, factor: number) => _complexLogic(baseCount, factor, randomFunc)

    const baseViewing = generateCount(baseCountForViewing, factorForViewing * viewingVariation)

    const soldCount = generateCount(baseCountForSold, factorForSold)
    const repeatPurchaseCount = generateCount(baseCountForRepeat, factorForRepeat)
    const currentlyViewingCount = Math.max(1, baseViewing)

    // Create and cache the metrics
    const metrics = { soldCount, repeatPurchaseCount, currentlyViewingCount }
    metricsCache.value[productId] = metrics

    return metrics
  }

  return {
    metricsCache,
    getMetricsForProduct,
  }
})
